import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useIsMobile, useIsVerified } from '../../utils/hooks'
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import dashboardApi from '../../api/dashboardApi';
import vendorApi from '../../api/vendorApi';
import userMetadataApi from '../../api/userMetadataApi';
import { CashflowMetricsResponse, TopExpensesResponse, TransactionFilterParams } from '../../api/types';
import Kpis from './sections/Kpis';
import Charts from './sections/Charts';
import ChatPanel from '../chat/ChatPanel';
import { ChevronLeft, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import ChatButton from '../../components/ChatButton'
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';
import WeeklyDrilldownModal from '../margin/components/WeeklyDrilldownModal';
import CurrentBalanceModal from './components/CurrentBalanceModal';
import { addDays, formatDateKey, formatShortMonthDay, startOfWeekMonday } from '../../utils/tutorialDates';
import { isTutorialRuntimeActive } from '../../components/tutorial/tutorialRuntime';

// Subpages
import Payables from './subpages/Payables'
import Receivables from './subpages/Receivables'

const STALE_TIME = 1000 * 60 * 5;    // Refetch on tab refocus every 5 min
const REFETCH_TIME = 1000 * 60 * 10; // Force refetch every 10 mins
// Refetch every time new data is sent (eg. add accounts)

const buildDashboardTutorialCashflow = (today: Date = new Date()): CashflowMetricsResponse => {
    const currentWeek = startOfWeekMonday(today);
    const trendWeeks = [-3, -2, -1, 0].map((offset) => addDays(currentWeek, offset * 7));
    const outlookWeeks = [-2, -1, 0, 1, 2, 3, 4, 5].map((offset) => addDays(currentWeek, offset * 7));
    const forecastWeeks = outlookWeeks.map((start) => ({
        start,
        end: addDays(start, 6),
        label: formatShortMonthDay(start),
    }));

    return {
        kpi_metrics: {
            current_balance: '$580,500.00',
            cash_in: '+$555,200.00',
            cash_out: '-$320,400.00',
            net_cash_flow: '$234,800.00',
        },
        summary_30d: {
            current_balance: '$580,500.00',
            cash_in: '+$555,200.00',
            cash_out: '-$320,400.00',
            net_cash_flow: '$234,800.00',
        },
        summary_60d: {
            current_balance: '$580,500.00',
            cash_in: '+$555,200.00',
            cash_out: '-$320,400.00',
            net_cash_flow: '$234,800.00',
        },
        summary_90d: {
            current_balance: '$580,500.00',
            cash_in: '+$555,200.00',
            cash_out: '-$320,400.00',
            net_cash_flow: '$234,800.00',
        },
        cash_in_cash_out: {
            '30': [
                { date: formatDateKey(trendWeeks[0]), cashIn: 45000, cashOut: 59000 },
                { date: formatDateKey(trendWeeks[1]), cashIn: 50500, cashOut: 52000 },
                { date: formatDateKey(trendWeeks[2]), cashIn: 46500, cashOut: 38500 },
                { date: formatDateKey(trendWeeks[3]), cashIn: 33500, cashOut: 18000 },
            ],
            '60': [
                { date: formatDateKey(trendWeeks[0]), cashIn: 45000, cashOut: 59000 },
                { date: formatDateKey(trendWeeks[1]), cashIn: 50500, cashOut: 52000 },
                { date: formatDateKey(trendWeeks[2]), cashIn: 46500, cashOut: 38500 },
                { date: formatDateKey(trendWeeks[3]), cashIn: 33500, cashOut: 18000 },
            ],
            '90': [
                { date: formatDateKey(trendWeeks[0]), cashIn: 45000, cashOut: 59000 },
                { date: formatDateKey(trendWeeks[1]), cashIn: 50500, cashOut: 52000 },
                { date: formatDateKey(trendWeeks[2]), cashIn: 46500, cashOut: 38500 },
                { date: formatDateKey(trendWeeks[3]), cashIn: 33500, cashOut: 18000 },
            ],
        },
        cash_outlook_current_next_month_v1: [
            { week_label: forecastWeeks[0].label, week_start: formatDateKey(forecastWeeks[0].start), week_ending: formatDateKey(forecastWeeks[0].end), actual_inflow: 42000, actual_outflow: 23000, actual_balance: 61000, projected_inflow: null, projected_outflow: null, projected_balance: null, type: 'historical' },
            { week_label: forecastWeeks[1].label, week_start: formatDateKey(forecastWeeks[1].start), week_ending: formatDateKey(forecastWeeks[1].end), actual_inflow: 52000, actual_outflow: 26000, actual_balance: 82000, projected_inflow: null, projected_outflow: null, projected_balance: null, type: 'historical' },
            { week_label: forecastWeeks[2].label, week_start: formatDateKey(forecastWeeks[2].start), week_ending: formatDateKey(forecastWeeks[2].end), actual_inflow: 39000, actual_outflow: 24000, actual_balance: 78000, projected_inflow: null, projected_outflow: null, projected_balance: 80000, type: 'current' },
            { week_label: forecastWeeks[3].label, week_start: formatDateKey(forecastWeeks[3].start), week_ending: formatDateKey(forecastWeeks[3].end), actual_inflow: null, actual_outflow: null, actual_balance: null, projected_inflow: 36000, projected_outflow: 21000, projected_balance: 95000, type: 'future' },
            { week_label: forecastWeeks[4].label, week_start: formatDateKey(forecastWeeks[4].start), week_ending: formatDateKey(forecastWeeks[4].end), actual_inflow: null, actual_outflow: null, actual_balance: null, projected_inflow: 41000, projected_outflow: 26000, projected_balance: 110000, type: 'future' },
            { week_label: forecastWeeks[5].label, week_start: formatDateKey(forecastWeeks[5].start), week_ending: formatDateKey(forecastWeeks[5].end), actual_inflow: null, actual_outflow: null, actual_balance: null, projected_inflow: 38000, projected_outflow: 25000, projected_balance: 123000, type: 'future' },
            { week_label: forecastWeeks[6].label, week_start: formatDateKey(forecastWeeks[6].start), week_ending: formatDateKey(forecastWeeks[6].end), actual_inflow: null, actual_outflow: null, actual_balance: null, projected_inflow: 44000, projected_outflow: 27000, projected_balance: 140000, type: 'future' },
            { week_label: forecastWeeks[7].label, week_start: formatDateKey(forecastWeeks[7].start), week_ending: formatDateKey(forecastWeeks[7].end), actual_inflow: null, actual_outflow: null, actual_balance: null, projected_inflow: 42000, projected_outflow: 24000, projected_balance: 158000, type: 'future' },
        ],
        forecast: [
            { date: formatDateKey(forecastWeeks[0].start), actual: 61000, balance: 61000 },
            { date: formatDateKey(forecastWeeks[1].start), actual: 82000, balance: 82000 },
            { date: formatDateKey(forecastWeeks[2].start), actual: 78000, projected: 80000, balance: 78000 },
            { date: formatDateKey(forecastWeeks[3].start), projected: 95000, balance: 95000 },
            { date: formatDateKey(forecastWeeks[4].start), projected: 110000, balance: 110000 },
            { date: formatDateKey(forecastWeeks[5].start), projected: 123000, balance: 123000 },
            { date: formatDateKey(forecastWeeks[6].start), projected: 140000, balance: 140000 },
            { date: formatDateKey(forecastWeeks[7].start), projected: 158000, balance: 158000 },
        ],
        cash_sources: [],
        weekly_forecast: [],
        expense_categories: [],
        receivables: [],
        payables: [],
        cash_outlook_13w_unconstrained: [
            { week_number: 1, week_label: forecastWeeks[3].label, week_start: formatDateKey(forecastWeeks[3].start), week_ending: formatDateKey(forecastWeeks[3].end), receipts: 36000, fixed_outflows: 14000, flexible_outflows: 5000, discretionary_outflows: 2000, net_cash: 15000, beginning_cash: 80000, ending_cash: 95000, confidence: 'high' },
            { week_number: 2, week_label: forecastWeeks[4].label, week_start: formatDateKey(forecastWeeks[4].start), week_ending: formatDateKey(forecastWeeks[4].end), receipts: 41000, fixed_outflows: 18000, flexible_outflows: 6000, discretionary_outflows: 2000, net_cash: 15000, beginning_cash: 95000, ending_cash: 110000, confidence: 'high' },
            { week_number: 3, week_label: forecastWeeks[5].label, week_start: formatDateKey(forecastWeeks[5].start), week_ending: formatDateKey(forecastWeeks[5].end), receipts: 38000, fixed_outflows: 16000, flexible_outflows: 7000, discretionary_outflows: 2000, net_cash: 13000, beginning_cash: 110000, ending_cash: 123000, confidence: 'medium' },
            { week_number: 4, week_label: forecastWeeks[6].label, week_start: formatDateKey(forecastWeeks[6].start), week_ending: formatDateKey(forecastWeeks[6].end), receipts: 44000, fixed_outflows: 19000, flexible_outflows: 6000, discretionary_outflows: 2000, net_cash: 17000, beginning_cash: 123000, ending_cash: 140000, confidence: 'medium' },
            { week_number: 5, week_label: forecastWeeks[7].label, week_start: formatDateKey(forecastWeeks[7].start), week_ending: formatDateKey(forecastWeeks[7].end), receipts: 42000, fixed_outflows: 16000, flexible_outflows: 6000, discretionary_outflows: 2000, net_cash: 18000, beginning_cash: 140000, ending_cash: 158000, confidence: 'medium' },
        ],
        as_of_date: formatDateKey(today),
        cashflow_calendar: [
            { vendor_name: 'Vendor A', expected_week: formatDateKey(addDays(today, 4)), amount: 800 },
            { vendor_name: 'Vendor B', expected_week: formatDateKey(addDays(today, -3)), amount: 13568 },
            { vendor_name: 'Vendor C', expected_week: formatDateKey(addDays(today, 8)), amount: 320 },
            { vendor_name: 'Vendor D', expected_week: formatDateKey(addDays(today, 11)), amount: 11.99 },
        ],
    } as CashflowMetricsResponse;
};

const DASHBOARD_TUTORIAL_TOP_EXPENSES: TopExpensesResponse = {
    last_30d: [
        { vendor_name: 'Vendor A', amount: 51630, category: 'Labor', account: 'Payroll' },
        { vendor_name: 'Vendor B', amount: 20000, category: 'Fixed Expenses', account: 'Rent' },
        { vendor_name: 'Vendor C', amount: 11008, category: 'Taxes', account: 'Sales Tax' },
        { vendor_name: 'Vendor D', amount: 6105, category: 'Fixed Expense', account: 'Utilities' },
        { vendor_name: 'Vendor E', amount: 5908, category: 'COGS', account: 'Inventory' },
    ],
    last_60d: [
        { vendor_name: 'Vendor A', amount: 51630, category: 'Labor', account: 'Payroll' },
        { vendor_name: 'Vendor B', amount: 20000, category: 'Fixed Expenses', account: 'Rent' },
        { vendor_name: 'Vendor C', amount: 11008, category: 'Taxes', account: 'Sales Tax' },
        { vendor_name: 'Vendor D', amount: 6105, category: 'Fixed Expense', account: 'Utilities' },
        { vendor_name: 'Vendor E', amount: 5908, category: 'COGS', account: 'Inventory' },
    ],
    last_90d: [
        { vendor_name: 'Vendor A', amount: 51630, category: 'Labor', account: 'Payroll' },
        { vendor_name: 'Vendor B', amount: 20000, category: 'Fixed Expenses', account: 'Rent' },
        { vendor_name: 'Vendor C', amount: 11008, category: 'Taxes', account: 'Sales Tax' },
        { vendor_name: 'Vendor D', amount: 6105, category: 'Fixed Expense', account: 'Utilities' },
        { vendor_name: 'Vendor E', amount: 5908, category: 'COGS', account: 'Inventory' },
    ],
};

export default function DashboardV2() {
    const isMobile = useIsMobile()
    const queryClient = useQueryClient();
    const { isAdmin } = useUser();
    const { workspaces, activeBusinessId, setActiveBusinessId } = useWorkspace();
    const [page, setPage] = useState("dashboard")
    const [showChat, setShowChat] = useState(!isMobile)
    const [extendChat, setExtendChat] = useState(false)
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false)
    const isVerified = useIsVerified()

    useEffect(() => {
        if (isMobile) {
            setShowChat(false);
            setExtendChat(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);

    const [business, setBusiness] = useState('Select Business')
    const [selectedBusinessId, setSelectedBusinessId] = useState<string | undefined>(undefined);
    const [days, setDays] = useState('30')
    const [showDaysFilter, setShowDaysFilter] = useState(false)
    const [isCalculating, setIsCalculating] = useState(false)
    const [isTutorialDemo, setIsTutorialDemo] = useState(() => isTutorialRuntimeActive('dashboard'))
    const [kpiDrilldown, setKpiDrilldown] = useState<{
        title: string;
        amount: number;
        initialFilters: Partial<TransactionFilterParams>;
        startDate: string;
        endDate: string;
    } | null>(null);
    const [isCurrentBalanceOpen, setIsCurrentBalanceOpen] = useState(false);
    const { data: allBusinessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: !!isAdmin,
    });

    const userBusinessOptions = Array.from(
        new Map(
            workspaces.flatMap((workspace) =>
                (workspace.businesses || []).map((business) => [
                    business.id,
                    {
                        id: business.id,
                        name: business.legal_name || business.dba_name || 'Unnamed Business',
                    },
                ]),
            ),
        ).values(),
    );
    const adminBusinessOptions = allBusinessesData?.businesses.map((b) => ({
        id: b.business_id || b.user_id,
        name: b.legal_name || b.dba_name || b.onboarding?.qbo_metadata?.company_name || b.user_email || 'Unknown',
        subtitle: b.user_email || undefined,
    })) || [];
    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;

    useEffect(() => {
        if (activeBusinessId) {
            setSelectedBusinessId(activeBusinessId);
            const biz = businessOptions.find(b => b.id === activeBusinessId);
            if (biz) setBusiness(biz.name);
        }
    }, [activeBusinessId, businessOptions]);
    const effectiveBusinessId = selectedBusinessId || activeBusinessId || undefined;

    useEffect(() => {
        const handleTutorialStep = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'dashboard') setIsTutorialDemo(true);
        };
        const handleTutorialActive = (event: Event) => {
            const detail = (event as CustomEvent<{ pageKey?: string; active?: boolean }>).detail;
            setIsTutorialDemo(detail?.active !== false && detail?.pageKey === 'dashboard');
        };
        const handleTutorialClosed = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'dashboard') setIsTutorialDemo(false);
        };
        if (isTutorialRuntimeActive('dashboard')) setIsTutorialDemo(true);
        window.addEventListener('finz:tutorial:active', handleTutorialActive);
        window.addEventListener('finz:tutorial:step', handleTutorialStep);
        window.addEventListener('finz:tutorial:closed', handleTutorialClosed);
        return () => {
            window.removeEventListener('finz:tutorial:active', handleTutorialActive);
            window.removeEventListener('finz:tutorial:step', handleTutorialStep);
            window.removeEventListener('finz:tutorial:closed', handleTutorialClosed);
        };
    }, []);

    const desktopDaysDropdownRef = useRef<HTMLDivElement>(null);
    const mobileDaysDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // 2. Type the event as MouseEvent
        function handleClickOutside(event: MouseEvent) {
            // 3. Type cast event.target so 'contains' knows what it's looking at
            const target = event.target as Node;
            if (
                !desktopDaysDropdownRef.current?.contains(target)
                && !mobileDaysDropdownRef.current?.contains(target)
            ) {
                setShowDaysFilter(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // KPI Data
    const {
        data: cashflowData,
        refetch: refetchCashflowData,
    } = useQuery<CashflowMetricsResponse>({
        queryKey: ['cashflowData', effectiveBusinessId],
        queryFn: () => dashboardApi.getCashflowMetrics(effectiveBusinessId),
        staleTime: STALE_TIME,
        refetchInterval: REFETCH_TIME,
        retry: false,
        enabled: !!effectiveBusinessId
    });

    // Top Expenses Data
    const { data: topExpensesData, refetch: refetchTopExpenses } = useQuery<TopExpensesResponse>({
        queryKey: ['topExpenses', effectiveBusinessId, days],
        queryFn: () => vendorApi.getTopExpenses(effectiveBusinessId),
        staleTime: STALE_TIME,
        enabled: !!effectiveBusinessId
    });
    const dashboardTutorialCashflow = useMemo(() => buildDashboardTutorialCashflow(), []);
    const displayCashflowData = isTutorialDemo ? dashboardTutorialCashflow : cashflowData;
    const displayTopExpensesData = isTutorialDemo ? DASHBOARD_TUTORIAL_TOP_EXPENSES : topExpensesData;

    const handleSubPage = (page: string) => {
        setPage(page)
    }

    const handleShowChat = () => {
        setShowChat(true)
    }

    // INSERT ENDPOINTS WITH QUERY
    // ...

    const handleRefresh = async () => {
        try {
            setIsCalculating(true);
            await dashboardApi.calculateCashflow(effectiveBusinessId);
            // Invalidate cache so refetch pulls fresh data from backend (avoids stale React Query cache)
            await queryClient.invalidateQueries({ queryKey: ['cashflowData', effectiveBusinessId] });
            await queryClient.invalidateQueries({ queryKey: ['topExpenses'] });
            await refetchCashflowData();
            await refetchTopExpenses();
        } catch (err) {
            console.error('Failed to trigger cashflow calculation', err);
        } finally {
            setIsCalculating(false);
        }
    }

    // Auto-refresh cashflow on login so user always sees correct chart
    useEffect(() => {
        const isJustLoggedIn = sessionStorage.getItem('just_logged_in') === 'true';
        const refreshStarted = sessionStorage.getItem('login_cashflow_refresh_started') === 'true';
        const businessId = effectiveBusinessId;
        if (isJustLoggedIn && !refreshStarted && businessId) {
            sessionStorage.setItem('login_cashflow_refresh_started', 'true');
            handleRefresh().catch(() => { });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveBusinessId]);

    const activeSummary = days === '60'
        ? displayCashflowData?.summary_60d
        : days === '90'
            ? displayCashflowData?.summary_90d
            : displayCashflowData?.summary_30d || displayCashflowData?.kpi_metrics;

    const formatDateKey = (value: Date): string => {
        const year = value.getFullYear();
        const month = `${value.getMonth() + 1}`.padStart(2, '0');
        const day = `${value.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getKpiDateRange = () => {
        const periodDays = Number(days) || 30;
        const asOfDateValue = displayCashflowData?.as_of_date;
        const end = asOfDateValue ? new Date(`${asOfDateValue}T00:00:00`) : new Date();
        end.setHours(0, 0, 0, 0);
        const start = new Date(end);
        start.setDate(end.getDate() - (periodDays - 1));
        return {
            startDate: formatDateKey(start),
            endDate: formatDateKey(end),
        };
    };

    const parseCurrencyValue = (value?: string) => {
        if (!value) return 0;
        const normalized = value.replace(/[^0-9.-]/g, '');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const openCashKpiDrilldown = (title: string, direction: 'inflow' | 'outflow', amountText?: string) => {
        const { startDate, endDate } = getKpiDateRange();
        setKpiDrilldown({
            title,
            amount: parseCurrencyValue(amountText),
            initialFilters: {
                direction,
                operating_flag: true,
            },
            startDate,
            endDate,
        });
    };

    return (
        (page === "dashboard") ? (
            <div className="flex h-full overflow-hidden relative">

                {!isVerified && (
                    <div className="disabled-view" />
                )}

                {/* MAIN DASHBOARD */}
                <div className={`overflow-auto flex-1 ${showChat && !showSetupGuidePanel && !isMobile ? 'w-[70%]' : 'w-full'}`}>
                    <div className="min-w-0 mx-5 mb-5 mt-[16px] space-y-6">
                        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="flex items-center justify-between gap-3">
                                <h1 className="pt-0.5 text-2xl font-semibold">Cash</h1>
                                <div className="flex items-center gap-2 sm:hidden">
                                    <div className="relative flex h-10 items-center rounded border border-gray-300 bg-white px-3 shadow-sm" ref={mobileDaysDropdownRef}>
                                        <button
                                            className="flex h-full items-center justify-between gap-2 text-sm leading-none focus:outline-none"
                                            onClick={() => setShowDaysFilter(!showDaysFilter)}
                                        >
                                            <span>{days}d</span>
                                            <ChevronDown className="h-4 w-4" />
                                        </button>

                                        {showDaysFilter && (
                                            <div className="dropdown-container absolute right-0 top-12 z-50 overflow-hidden rounded border bg-white shadow-lg">
                                                {['30', '60', '90'].map((value) => (
                                                    <button
                                                        key={value}
                                                        className="flex h-10 w-full items-center whitespace-nowrap px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100 focus:outline-none"
                                                        onClick={() => {
                                                            setDays(value);
                                                            setShowDaysFilter(false);
                                                        }}
                                                    >
                                                        {value} days
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleRefresh}
                                        disabled={isCalculating}
                                        aria-label="Refresh"
                                        title="Refresh"
                                        className={`relative flex h-10 w-10 items-center justify-center rounded border text-sm shadow-sm ${isCalculating ? 'bg-gray-300 text-gray-700' : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}
                                    >
                                        <RefreshCw size={16} className={isCalculating ? 'animate-spin' : ''} />
                                    </button>
                                </div>
                            </div>
                            {/* FILTERS */}
                            <div className="flex w-full flex-wrap items-stretch justify-end gap-3 lg:w-auto lg:items-start">

                                {/* 1. Attach the ref to the parent container */}
                                <div className="relative hidden w-full items-center rounded border border-gray-300 px-4 py-3.5 shadow-sm sm:flex sm:w-auto" ref={desktopDaysDropdownRef}>
                                    <button
                                        className="flex h-full w-full items-center justify-between gap-2 text-sm leading-none focus:outline-none sm:w-auto"
                                        onClick={() => setShowDaysFilter(!showDaysFilter)}
                                    >
                                        <span>Last {days} Days</span>
                                        <ChevronDown className="w-4 h-4" />
                                    </button>

                                    {showDaysFilter && (
                                        <div className="dropdown-container top-12 right-0 absolute bg-white border rounded shadow-lg z-50 overflow-hidden">
                                            {['30', '60', '90'].map((value) => (
                                                <button
                                                    key={value}
                                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none transition-colors flex items-center h-10"
                                                    onClick={() => {
                                                        setDays(value);
                                                        setShowDaysFilter(false);
                                                    }}
                                                >
                                                    {value} Days
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {businessOptions.length > 1 && (
                                    <SearchableBusinessSelect
                                        options={businessOptions}
                                        value={activeBusinessId || selectedBusinessId}
                                        onChange={(id) => {
                                            const option = businessOptions.find(b => b.id === id);
                                            setBusiness(option?.name || 'Select Business');
                                            setSelectedBusinessId(id);
                                            setActiveBusinessId(id);
                                        }}
                                        widthClassName="w-full sm:w-80"
                                        showSubtitleWhenClosed={false}
                                    />)}
                                <div className="hidden sm:block sm:w-auto">
                                    <button
                                        onClick={handleRefresh}
                                        aria-label="Refresh"
                                        title="Refresh"
                                        className={`relative flex h-11 w-10 items-center justify-center rounded border text-sm shadow-sm sm:w-auto sm:px-6 ${isCalculating ? 'bg-gray-300 text-gray-700' : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}>
                                        <span>{isCalculating ? 'Calculating...' : 'Refresh'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>



                        {/* MAIN SECTIONS */}
                        <div className="flex flex-col gap-4 ">
                            <Kpis
                                data={activeSummary}
                                onCurrentBalanceClick={() => setIsCurrentBalanceOpen(true)}
                                onCashInClick={() => openCashKpiDrilldown('Cash In Transactions', 'inflow', activeSummary?.cash_in)}
                                onCashOutClick={() => openCashKpiDrilldown('Cash Out Transactions', 'outflow', activeSummary?.cash_out)}
                            />
                            <Charts forecastData={displayCashflowData && displayCashflowData.forecast}
                                cashData={displayCashflowData?.cash_in_cash_out}
                                cashSourcesData={displayCashflowData && displayCashflowData.cash_sources}
                                cashOutlookData={displayCashflowData?.cash_outlook_13w_unconstrained}
                                topExpensesData={displayTopExpensesData}
                                rawData={displayCashflowData}
                                daysFilter={days}
                                businessId={selectedBusinessId}
                            />
                        </div>
                    </div>
                </div>

                {!isAdmin && showSetupGuidePanel && !isMobile && (
                    <div
                        aria-hidden="true"
                        className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                    />
                )}

                {/* Chat Panel */}
                {!isAdmin && <div className={`${showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-[1999] flex bg-white w-full max-w-none h-[100dvh]' : 'flex bg-white') : 'hidden'} ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'} chat-panel`}>
                    {/* Chat */}
                    <div className="flex-1 overflow-auto flex flex-col">
                        <ChatPanel
                            page="cashflow"
                            onClose={() => setShowChat(false)}
                        />
                    </div>
                    {/* Extend Button */}
                    {!isMobile && (
                        <div onClick={() => setExtendChat(!extendChat)} className="border hover:bg-gray-200 hover:cursor-pointer py-5 rounded absolute bg-gray-100 top-20 -left-2">
                            {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                        </div>
                    )}
                </div>}

                {/* Chat Toggle Button */}
                {(!showChat && !showSetupGuidePanel && !isAdmin) && <ChatButton handleShowChat={handleShowChat} />}

                <WeeklyDrilldownModal
                    isOpen={!!kpiDrilldown}
                    onClose={() => setKpiDrilldown(null)}
                    title={kpiDrilldown?.title || ''}
                    startDate={kpiDrilldown?.startDate || ''}
                    endDate={kpiDrilldown?.endDate || ''}
                    startAmount={kpiDrilldown?.amount || 0}
                    initialFilters={kpiDrilldown?.initialFilters}
                    businessId={selectedBusinessId}
                />

                <CurrentBalanceModal
                    isOpen={isCurrentBalanceOpen}
                    onClose={() => setIsCurrentBalanceOpen(false)}
                    title="Current Balance Details"
                    businessId={selectedBusinessId}
                    currentBalanceTotal={parseCurrencyValue(activeSummary?.current_balance)}
                />
            </div>
        ) :

            // PAYMENTS PAGE ---------------------------------
            (page === 'payables') ? <Payables handleSubPage={handleSubPage} data={undefined} /> :

                // RECEIVABLES PAGE ---------------------------------
                (page === 'receivables') ? <Receivables handleSubPage={handleSubPage} data={cashflowData && cashflowData.receivables} /> : <div></div>
    )
}
